/**
 * Mutaciones local-first del PROGRESO DE VISIONADO de vídeo (F2-B).
 *
 * Escriben en la BD PowerSync local (Drizzle); la cola de sync las envía al
 * servidor (`db/sync.rs::video_watch_progress`), que FUERZA `user_id` al actor y
 * mantiene `watched_pct` MONOTÓNICO. El id es DETERMINISTA `${userId}:${videoId}`
 * → un único registro por (usuario, vídeo), reanudable offline.
 *
 * La monotonía se aplica también en cliente (no bajar el pct localmente) para
 * que la UI de "reanudar / completado" sea coherente incluso antes de sincronizar.
 */

import { eq, type PowerSyncDrizzleDB, video_watch_progress } from "@space/powersync";
import { nowISO } from "../utils/date-helpers";

/** Id determinista de una fila de progreso de visionado. */
export function watchProgressId(userId: string, videoId: string): string {
  return `${userId}:${videoId}`;
}

/** Clampa un porcentaje a [0,1]. */
export function clampPct(pct: number): number {
  if (Number.isNaN(pct)) {
    return 0;
  }
  return Math.min(1, Math.max(0, pct));
}

export interface WatchProgressUpdate {
  /** Posición actual del cursor (segundos). Sigue al cliente (puede bajar). */
  positionSeconds: number;
  /** Porcentaje visto [0,1]. Se aplica MONOTÓNICO (max con el previo). */
  watchedPct: number;
  /** ¿El cliente considera el vídeo completado (cruzó minWatchPct)? */
  completed?: boolean;
}

/** Fila (subset) leída de la BD local para el cálculo monotónico. */
interface ExistingRow {
  watched_pct: number | null;
  completed_at: string | null;
}

/**
 * Upsert local-first del progreso de visionado. Determinista e idempotente:
 *  - id = `${userId}:${videoId}`.
 *  - `watched_pct` nunca baja (max con el valor local previo).
 *  - `completed_at` se sella una vez y se conserva.
 */
export async function upsertWatchProgress(
  db: PowerSyncDrizzleDB,
  userId: string,
  videoId: string,
  update: WatchProgressUpdate,
): Promise<string> {
  const id = watchProgressId(userId, videoId);
  const now = nowISO();
  const nextPct = clampPct(update.watchedPct);
  const position = Math.max(0, update.positionSeconds || 0);

  const existing = (await db
    .select({
      watched_pct: video_watch_progress.watched_pct,
      completed_at: video_watch_progress.completed_at,
    })
    .from(video_watch_progress)
    .where(eq(video_watch_progress.id, id))
    .limit(1)) as ExistingRow[];

  const prev = existing[0];
  const monotonicPct = prev ? Math.max(prev.watched_pct ?? 0, nextPct) : nextPct;
  // completed_at: se conserva si ya existía; se sella ahora si el cliente lo marca.
  const completedAt = prev?.completed_at ?? (update.completed ? now : null);

  if (prev) {
    await db
      .update(video_watch_progress)
      .set({
        position_seconds: position,
        watched_pct: monotonicPct,
        completed_at: completedAt,
        updated_at: now,
      })
      .where(eq(video_watch_progress.id, id));
  } else {
    await db.insert(video_watch_progress).values({
      id,
      user_id: userId,
      video_id: videoId,
      position_seconds: position,
      watched_pct: monotonicPct,
      completed_at: completedAt,
      created_at: now,
      updated_at: now,
    });
  }
  return id;
}

/**
 * ¿El progreso cumple el umbral de completitud del bloque? Puro. Alimenta el
 * grade del bloque `video` (mirror del `minWatchPct`, default 0.9).
 */
export function isWatchComplete(watchedPct: number, minWatchPct: number): boolean {
  return clampPct(watchedPct) >= clampPct(minWatchPct);
}
